import React, { useEffect, useRef, useState, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router";
import axios from "axios";
import _ from "lodash";
import SuccessModal from "../shared/SuccessModal";
import SkillsSearchDropdown from "../Dropdowns/SkillsSearchDropdown";
import CitySearchDropdown from "../Dropdowns/CitySearchDropdown";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const EditProfile = ({ profileData, fullProfile, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const navigate = useNavigate();
  const userid = localStorage.getItem("userid");
  const [showModal, setShowModal] = useState(false);
  const [degrees, setDegrees] = useState([]);
  const [showExperience, setShowExperience] = useState(false);
  const [showSocialLinks, setShowSocialLinks] = useState(false);
  const [showEducation, setShowEducation] = useState(false);
  const [showPersonal, setShowPersonal] = useState(true);
  const [showPreference, setShowPreference] = useState(false);

  const [modalConfig, setModalConfig] = useState({
    success: false,
    message: "",
    onClose: () => setShowModal(false)
  });
  const initialValuesCandidate = {
    userid: userid,
    city: profileData?.city || "",
    hometown: profileData?.hometown || "",
    college: profileData?.college || "",
    university: profileData?.university || "",
    passing_year: profileData?.passing_year || "",
    degree: profileData?.branch || "",
    linkedinid: profileData?.linkedinid || "",
    githubid: profileData?.githubid || "",
    portfolio: profileData?.portfolio || "",
    currentcompany: profileData?.currentcompany || "",
    yearsOfExperience: profileData?.yearsOfExperience || "",
    currentDesignation: profileData?.currentDesignation || "",
    noticePeriod: profileData?.noticePeriod || "",
    currentSalary: profileData?.currentSalary || "",
    skills: profileData?.skills || "",
    dob: profileData?.dob || "",
    workLocation: profileData?.workLocation || "",
    typePartFull: profileData?.typePartFull || "",
    typeFullIntern: profileData?.typeFullIntern || "",
    jobType: profileData?.jobType || "",
    uploadCV: profileData?.uploadCV || "",
    isactive: profileData?.isactive || true,
    whatsapp_mobile: profileData?.whatsapp_mobile || "",
    gender: profileData?.gender || "",
    language: Array.isArray(profileData?.language)
      ? profileData.language
      : typeof profileData?.language === "string"
      ? profileData.language.split(",").map((l) => l.trim())
      : [],
    country: profileData?.country || "",
    state: profileData?.state || "",
    address: profileData?.address || "",
    bio: profileData?.bio || "",
  };

  const [updateData, setUpdateData] = useState(initialValuesCandidate);

  const validationSchema = Yup.object({
    city: Yup.string().required("Please enter city"),
    language: Yup.array().min(1, "Please select at least one language"),
    hometown: Yup.string().required("Please enter hometown"),
    college: Yup.string().required("Please enter your college name"),
    university: Yup.string().required("Please enter your university name"),
    passing_year: Yup.number()
      .typeError("Please enter a valid year")
      .required("Please enter passing year"),
    degree: Yup.string().required("Please enter degree"),
    dob: Yup.string().required("Please enter date of birth"),
    workLocation: Yup.string(),
    jobType: Yup.string().required("Please select a job type"),
    gender: Yup.string().required("Please select gender"),
    country: Yup.string().required("Please enter your country"),
    state: Yup.string().required("Please enter your state"),
    bio: Yup.string().required("Please enter bio"),
    whatsapp_mobile: Yup.number()
      .typeError("Please enter a valid number")
      .required("Please enter your mobile number"),
    linkedinid: Yup.string()
      .matches(
        /^(https?:\/\/)?(www\.)?linkedin\.com\/.+$/,
        "Please enter a valid LinkedIn URL"
      ),
    githubid: Yup.string()
      .matches(
        /^(https?:\/\/)?(www\.)?github\.com\/.+$/,
        "Please enter a valid GitHub URL"
      ),
      portfolio: Yup.string() 
      .matches(
        /^(https?:\/\/)?(www\.)?.+\..+$/,
        "Please enter a valid URL"
      ),
      uploadCV: Yup.mixed()
      .test('fileRequired', 'Please upload your CV', function(value) {
        return fullProfile?.uploadCV || value;
      })
      .test('fileFormat', 'Supported formats: PDF, DOC, DOCX', function (value) {
        if (!value || typeof value === 'string') return true; 
        const supportedFormats = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        return supportedFormats.includes(value.type);
      })
      .test('fileSize', 'File size must be less than 5MB', function (value) {
        if (!value || typeof value === 'string') return true;
        return value.size <= 5 * 1024 * 1024;
      })
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      Object.keys(values).forEach(key => {
        if (key === 'skills') {
          formData.append(key, JSON.stringify(values[key]));
        } else if (key === 'uploadCV') {
          if (values[key] && typeof values[key] !== "string") {
            formData.append('cv', values[key]);
          } 
          else if (profileData?.uploadCV) {
            console.log("Retaining existing CV:", profileData.uploadCV);
            formData.append('existingCV', profileData.uploadCV);
          }
        } else {
          formData.append(key, values[key]);
        }
      });
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}candidate_details/updatecandidate`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        setModalConfig({
          success: true,
          message: "Profile updated successfully!",
          onClose: () => {
            setShowModal(false);
            navigate("/myprofile", { replace: true });
            window.location.reload();
          }
        });
      }
      
    } catch (error) {
      console.error("Error updating profile:", error);
      setModalConfig({
        success: false,
        message: "Failed to update profile. Please try again.",
        onClose: () => setShowModal(false)
      });
    } finally {
      setLoading(false);
      setSubmitting(false)
      setShowModal(true);
    }
  };

  const fetchProfileData = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}candidate_details/showcandidatebyid/${userid}`;
      const response = await axios.get(url);
      const profileData = response.data[0];
      setUpdateData(profileData || initialValuesCandidate);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setIsFetching(false);
    }
  }, [userid, initialValuesCandidate]);

  const fetchAllSkills = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}skills/showallskills`);
      setSkills(response.data);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const fetchAllDegrees = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}degree/getdegrees`);
      setDegrees(response.data)
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchProfileData();
    fetchAllSkills();
    fetchAllDegrees();
  }, []);

  if (isFetching) {
    return <div>Loading...</div>;
  }

  return (
    <div className="col-lg-9 w-100">
      <div className="user-profile-wrapper">
        <div className="user-profile-card">
          <h4 className="user-profile-card-title">Edit Your Profile</h4>
          
          
          <div className="profile-resume">
            <div className="profile-form">
              <Formik
                enableReinitialize={true}
                initialValues={updateData || initialValuesCandidate}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, setFieldValue }) => (
                  <Form>
                    <div className="card p-3 mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPersonal(!showPersonal)}>
                        <h6 className="mb-0">Personal Details</h6>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => setShowPersonal(!showPersonal)}
                        >
                          {showPersonal ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                      {showPersonal && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Languages Known</label>
                              <div role="group" aria-labelledby="checkbox-group">
                                {["Hindi", "English", "Spanish", "Chinese"].map((lang) => (
                                  <label key={lang} className="me-3">
                                    <Field
                                      type="checkbox"
                                      name="language"
                                      value={lang}
                                    />
                                    <span className="ms-1">{lang}</span>
                                  </label>
                                ))}
                              </div>
                              <ErrorMessage name="language" component="div" className="text-danger" />
                            </div>

                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Whatsapp Mobile</label>
                              <Field
                                name="whatsapp_mobile"
                                type="number"
                                className="form-control"
                                placeholder="Enter your Whatsapp number"
                              />
                              <ErrorMessage name="whatsapp_mobile" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Date of Birth</label>
                              <Field
                                name="dob"
                                type="date"
                                className="form-control"
                                placeholder="Enter date of birth"
                              />
                              <ErrorMessage name="dob" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Gender</label>
                              <Field as="select" name="gender" className="form-control">
                                <option value="">Choose Gender</option>
                                <option value="1">Male</option>
                                <option value="2">Female</option>
                                <option value="3">Other</option>
                              </Field>
                              <ErrorMessage name="gender" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <Field name="city">
                                {({ field, form }) => (
                                  <CitySearchDropdown initialValue={form.values.city} name={field.name} label={field.name} />
                                )}
                              </Field>
                              <ErrorMessage name="city" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="form-group">
                              <label>Full Address</label>
                              <Field
                                name="address"
                                type="text"
                                className="form-control"
                                placeholder="Enter your full address"
                              />
                              <ErrorMessage name="address" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="form-group">
                              <label>Bio <span className="text-danger">*</span></label>
                              <Field
                                name="bio"
                                as="textarea"
                                className="form-control"
                                rows="5"
                              />
                              <ErrorMessage name="bio" component="div" className="text-danger" />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group">
                              <label>Upload CV</label>
                              <Field name="uploadCV">
                                {({ field, form }) => (
                                  <input
                                    type="file"
                                    className="form-control"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(event) =>
                                      setFieldValue("uploadCV", event.currentTarget.files[0])
                                    }
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="uploadCV"
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        </div>

                      )}
                    </div>
                    <div className="card p-3 mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowEducation(!showEducation)}>
                        <h6 className="mb-0">Education / Qualifications</h6>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                        >
                          {showEducation ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                      {showEducation && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>College</label>
                              <Field
                                name="college"
                                type="text"
                                className="form-control"
                                placeholder="Enter your college name"
                              />
                              <ErrorMessage name="college" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>University</label>
                              <Field
                                name="university"
                                type="text"
                                className="form-control"
                                placeholder="Enter your university name"
                              />
                              <ErrorMessage name="university" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Degree</label>
                              <Field as="select" name="degree" className="form-control">
                                <option value="">Choose Degree</option>
                                {degrees.map((degree) => (
                                  <option value={degree.degree_name} key={degree.d_id}>
                                    {degree.degree_name}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="degree" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Skills</label>
                              <Field name="skills">
                                {({ field, form }) => (
                                  <SkillsSearchDropdown
                                    value={form.values.skills || []}
                                    onChange={(newSkills) => form.setFieldValue("skills", newSkills)}
                                    skillsList={skills || []}
                                  />
                                )}
                              </Field>
                              <ErrorMessage name="skills" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Passing Year</label>
                              <Field as="select" name="passing_year" className="form-control">
                                <option value="">Select Passing Year</option>
                                {Array.from({ length: 2030 - 2015 + 1 }, (_, i) => 2015 + i).map((year) => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="passing_year" component="div" className="text-danger" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="card p-3 mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPreference(!showPreference)}>
                        <h6 className="mb-0">Working Preferences</h6>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                        >
                          {showPreference ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                      {showPreference && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Job Type</label>
                              <Field as="select" name="jobType" className="form-control">
                                <option value="">Choose Type</option>
                                <option value="1">Full Time</option>
                                <option value="2">Part Time</option>
                                <option value="3">Remote</option>
                              </Field>
                              <ErrorMessage name="jobType" component="div" className="text-danger" />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Notice Period (Days)</label>
                              <Field
                                name="noticePeriod"
                                type="text"
                                className="form-control"
                                placeholder="Enter your Notice Period"
                              />
                              <ErrorMessage
                                name="noticePeriod"
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="card p-3 mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowExperience(!showExperience)}>
                        <h6 className="mb-0">Last/current  Work Experience</h6>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                        >
                          {showExperience ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                      {showExperience && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Designation</label>
                              <Field
                                name="currentDesignation"
                                type="text"
                                className="form-control"
                                placeholder="Enter your current Designation"
                              />
                              <ErrorMessage name="currentDesignation" component="div" className="text-danger" />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Years Of Experience</label>
                              <Field
                                name="yearsOfExperience"
                                type="text"
                                className="form-control"
                                placeholder="Enter your experience"
                              />
                              <ErrorMessage name="yearsOfExperience" component="div" className="text-danger" />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Package (LPA)</label>
                              <Field
                                name="currentSalary"
                                type="text"
                                className="form-control"
                                placeholder="Enter your current Salary"
                              />
                              <ErrorMessage name="currentSalary" component="div" className="text-danger" />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Orgnization Company</label>
                              <Field
                                name="currentcompany"
                                type="text"
                                className="form-control"
                                placeholder="Enter your current company"
                              />
                              <ErrorMessage name="currentcompany" component="div" className="text-danger" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="card p-3 mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowSocialLinks(!showSocialLinks)}>
                        <h6 className="mb-0">Social Links</h6>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                        >
                          {showSocialLinks ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                      {showSocialLinks && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Here you can see my work</label>
                              <Field
                                name="githubid"
                                type="text"
                                className="form-control"
                                placeholder="like GitHub URL"
                              />
                              <ErrorMessage name="githubid" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Portfolio URL</label>
                              <Field
                                name="portfolio"
                                type="url"
                                className="form-control"
                                placeholder="Enter Portfolio URL"
                              />
                              <ErrorMessage name="portfolio" component="div" className="text-danger" />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>LinkedIn URL</label>
                              <Field
                                name="linkedinid"
                                type="url"
                                className="form-control"
                                placeholder="Enter LinkedIn Profile URL"
                              />
                              <ErrorMessage name="linkedinid" component="div" className="text-danger" />
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group mt-3">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || isSubmitting}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Updating...
                              </>
                            ) : (
                              "Update Profile"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
      <SuccessModal 
        show={showModal}
        onClose={modalConfig.onClose}
        success={modalConfig.success}
        message={modalConfig.message}
      />
    </div>
  );
};

export default EditProfile;